export type LayerFormProperty = {
  key: string;
  value: unknown;
  check: boolean;
};

type LayerPropertiesInput =
  | Record<string, unknown>
  | Array<{
      key: string;
      value: unknown;
      check?: boolean;
    }>;

const isLayerPropertiesArray = (
  properties: LayerPropertiesInput,
): properties is Array<{
  key: string;
  value: unknown;
  check?: boolean;
}> => Array.isArray(properties);

const getIndexedPropertyEntries = (properties: Record<string, unknown>) =>
  Object.entries(properties).filter(([key]) => /^\d+$/.test(key));

const isIndexedLayerPropertiesObject = (
  properties: Record<string, unknown>,
) => {
  const entries = getIndexedPropertyEntries(properties);

  return entries.some(([, value]) => {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'key' in value && 'value' in value;
  });
};

const isCorruptedFormPropertiesArray = (
  properties: Array<{
    key: string;
    value: unknown;
    check?: boolean;
  }>,
) =>
  properties.some(
    (property) =>
      /^\d+$/.test(property.key) &&
      property.value &&
      typeof property.value === 'object' &&
      'key' in property.value &&
      'value' in property.value,
  );

const unwrapIndexedPropertyEntries = (
  entries: Array<[string, unknown]>,
  checkProperties: string[],
): LayerFormProperty[] =>
  entries
    .map(([, property]) => property)
    .filter(
      (
        property,
      ): property is {
        key: string;
        value: unknown;
        check?: boolean;
      } =>
        Boolean(
          property &&
            typeof property === 'object' &&
            'key' in property &&
            'value' in property,
        ),
    )
    .map((property) => ({
      key: property.key,
      value: property.value,
      check: property.check ?? checkProperties.includes(property.key),
    }));

export const normalizeLayerPropertiesInput = ({
  properties,
  checkProperties = [],
}: {
  properties?: LayerPropertiesInput | null;
  checkProperties?: string[];
}): LayerFormProperty[] => {
  if (!properties) {
    return [];
  }

  if (isLayerPropertiesArray(properties)) {
    if (isCorruptedFormPropertiesArray(properties)) {
      return properties
        .filter(
          (property) =>
            /^\d+$/.test(property.key) &&
            property.value &&
            typeof property.value === 'object' &&
            'key' in property.value &&
            'value' in property.value,
        )
        .map((property) => ({
          key: (property.value as { key: string }).key,
          value: (property.value as { value: unknown }).value,
          check:
            (property.value as { check?: boolean }).check ??
            checkProperties.includes((property.value as { key: string }).key),
        }));
    }

    return properties.map((property) => ({
      key: property.key,
      value: property.value,
      check: property.check ?? checkProperties.includes(property.key),
    }));
  }

  if (isIndexedLayerPropertiesObject(properties as Record<string, unknown>)) {
    return unwrapIndexedPropertyEntries(
      getIndexedPropertyEntries(properties as Record<string, unknown>),
      checkProperties,
    );
  }

  return Object.entries(properties).map(([key, value]) => ({
    key,
    value,
    check: checkProperties.includes(key),
  }));
};

export const getRawLayerOrder = (layer: {
  properties?: LayerPropertiesInput | null;
}) => {
  const { properties } = layer;

  if (!properties) {
    return 0;
  }

  if (isLayerPropertiesArray(properties)) {
    const layerOrderProperty = properties.find(
      (property) => property.key === 'layerOrder',
    );

    return layerOrderProperty?.value ?? 0;
  }

  if (isIndexedLayerPropertiesObject(properties as Record<string, unknown>)) {
    const layerOrderProperty = getIndexedPropertyEntries(
      properties as Record<string, unknown>,
    )
      .map(([, property]) => property)
      .find((property: any) => property.key === 'layerOrder') as
      | { value?: number }
      | undefined;

    return layerOrderProperty?.value ?? 0;
  }

  return (properties as Record<string, unknown>).layerOrder ?? 0;
};
